import { useMemo, useState } from 'react'
import { create } from '@bufbuild/protobuf'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { useQuery } from '@connectrpc/connect-query'
import { keepPreviousData } from '@tanstack/react-query'
import { Activity, CheckSquare, CreditCard, FileText, Inbox, Mail, StickyNote, type LucideIcon } from 'lucide-react'
import { Skeleton } from 'skullet/react'

import { ActivityLiteSchema, type ActivityLite } from '@/gen/cortado/v1/actions_pb'
import { ActivityTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { listJobActivity } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListJobActivityRequestSchema } from '@/gen/cortado/v1/jobs_pb'
import { formatShortDate } from '@/lib/date-utils'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { jobActivityIconKey, jobActivityTitle, type JobActivityIconKey } from '@/components/pipeline/job-detail-view'

const PAGE_SIZE = 20

const ICONS: Record<JobActivityIconKey, LucideIcon> = {
  note: StickyNote,
  email: Mail,
  inquiry: Inbox,
  quote: FileText,
  payment: CreditCard,
  task: CheckSquare,
  default: Activity,
}

const SAMPLE_ACTIVITIES: ActivityLite[] = [
  { title: 'Quote sent', description: 'Sent Q-1042 to the client', type: ActivityTypeEnum.ACTIVITY_TYPE_QUOTE_SENT },
  { title: 'Note added', description: 'Confirmed the tasting date and dietary needs', type: ActivityTypeEnum.ACTIVITY_TYPE_NOTE_ADDED },
  { title: 'Inquiry received', description: 'Wedding reception for 120 guests', type: ActivityTypeEnum.ACTIVITY_TYPE_INQUIRY_CREATED },
].map(({ title, description, type }, i) =>
  create(ActivityLiteSchema, {
    id: `sample-${i}`,
    title,
    description,
    activityType: type,
    createdAt: timestampFromDate(new Date(2026, 0, i + 1)),
  }),
)

/**
 * The job's system timeline (`ListJobActivity`), one page at a time. The
 * previous page stays on screen while "Show more" refetches.
 */
function useJobFeed(jobId: string) {
  const [limit, setLimit] = useState(PAGE_SIZE)
  const request = useMemo(() => create(ListJobActivityRequestSchema, { jobId, limit }), [jobId, limit])
  const query = useQuery(listJobActivity, request, { enabled: Boolean(jobId), placeholderData: keepPreviousData })
  return {
    activities: query.data?.activities ?? [],
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    showMore: () => setLimit((current) => current + PAGE_SIZE),
  }
}

interface JobFeedListProps {
  activities: ActivityLite[]
  totalCount: number
  isLoading: boolean
  onShowMore: () => void
}

/** The feed rows with their "Show more" / "That is everything" footer. */
function JobFeedList({ activities, totalCount, isLoading, onShowMore }: JobFeedListProps) {
  return (
    <Skeleton
      data={isLoading ? null : activities}
      sample={SAMPLE_ACTIVITIES}
      label="Loading activity"
      empty={!isLoading && activities.length === 0}
      emptyMessage={<p className="px-6 pb-3 text-sm text-muted-foreground">No activity yet.</p>}
    >
      {(activities) => (
        <>
          <ul className="divide-y">
            {activities.map((activity) => {
              const Icon = ICONS[jobActivityIconKey(activity.activityType)]
              return (
                <li key={activity.id} className="flex items-start gap-3 px-6 py-3">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{jobActivityTitle(activity)}</div>
                    {activity.description && (
                      <div className="line-clamp-2 text-xs text-muted-foreground">{activity.description}</div>
                    )}
                  </div>
                  {activity.createdAt && (
                    <span className="shrink-0 font-mono text-xs text-muted-foreground">{formatShortDate(activity.createdAt)}</span>
                  )}
                </li>
              )
            })}
          </ul>
          <div className="border-t px-6 py-2.5 text-xs text-muted-foreground">
            {activities.length < totalCount ? (
              <button
                type="button"
                onClick={onShowMore}
                className="rounded-sm outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                Show more
              </button>
            ) : (
              <span>That is everything · {activities.length}</span>
            )}
          </div>
        </>
      )}
    </Skeleton>
  )
}

/** The job page rail's Feed card: the job's system timeline, newest first. */
export function JobFeedCard({ jobId }: { jobId: string }) {
  const feed = useJobFeed(jobId)

  return (
    <Card className="gap-3 pt-4">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Feed</CardTitle>
        <span className="text-xs tabular-nums text-muted-foreground">{feed.totalCount}</span>
      </CardHeader>
      <div>
        <JobFeedList
          activities={feed.activities}
          totalCount={feed.totalCount}
          isLoading={feed.isLoading}
          onShowMore={feed.showMore}
        />
      </div>
    </Card>
  )
}
